import type { Orientation } from '@/src/store/dicom-web/classification-typing';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { useMaskExtractor } from '@/src/composables/useMaskExtractor';
import { detectCardiacEsEdFromArea, useOCGCalculatorStore } from '@/src/store/calculators/OCGCalculatorStore';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import { getPolygonArea } from '@/src/store/contours/contour-processing/getPolygonArea';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

export interface EsEdValidationResult {
  isValid: boolean;
  errorMessage: string;
  typeOfError?: 'suggested_action' | 'validation_error';
  suggestedAction?: {
    action: 'crop';
    crop: {
      start: number; // 0-based index relative to breath-hold start
      end: number;
    };
  };
}

export interface SuggestedCropInfo {
  startPhase: number; // 0-based index relative to breath-hold start
  endPhase: number;
  startImageIndex: number; // global 0-based slice image index
  endImageIndex: number;
  startUID: string;
  endUID: string;
}

export interface SliceEsEdPrecheckResult {
  isValid: boolean;
  hasCropSuggestion: boolean;
  errorMessage: string;
  esIndices: number[];
  edIndices: number[];
  nRR: number;
  lvBpAreaRaw: number[];
  lvBpAreaSmoothed?: number[];
  excludedPhaseIndices?: number[];
  suggestedCrop?: SuggestedCropInfo;
}

export interface SlicePrecheckParams {
  viewerID: string;
  seriesIndex: number;
  sliceIndex: number;
  orientation: Orientation;
}

/**
 * Validate that the end-systole (ES) and end-diastole (ED) indices are consistent.
 * Port of backend `misc_functions.py:validate_es_ed_indices`.
 *
 * Rules:
 * - At least 2 ED indices (including boundaries)
 * - At least 1 ES index
 * - First ED index must be before first ES index
 * - Last ED index must be after last ES index
 * - Each ES index must be between two ED indices
 * - Indices must alternate and be in ascending order
 * - Edge case: cycle starting with ES instead of ED triggers a crop recommendation
 */
export function validateEsEdIndices(
  esIdx: number[],
  edIdx: number[],
  _nRR: number,
): EsEdValidationResult {
  if (edIdx.length < 2) {
    return {
      isValid: false,
      errorMessage: 'Validation Error: There should be at least 2 ED phases (including endpoints).',
      typeOfError: 'validation_error',
    };
  }

  if (esIdx.length < 1) {
    return {
      isValid: false,
      errorMessage: 'Validation Error: There should be at least 1 ES phase.',
      typeOfError: 'validation_error',
    };
  }

  if (edIdx[0] >= esIdx[0]) {
    return {
      isValid: false,
      errorMessage: 'Validation Error: The first ED phase should be before the first ES phase.',
      typeOfError: 'validation_error',
    };
  }

  if (edIdx[edIdx.length - 1] <= esIdx[esIdx.length - 1]) {
    return {
      isValid: false,
      errorMessage: 'Validation Error: The last ED phase should be after the last ES phase.',
      typeOfError: 'validation_error',
    };
  }

  // Check that each ES index is between two ED indices
  for (const es of esIdx) {
    if (!edIdx.some(ed => ed < es) || !edIdx.some(ed => ed > es)) {
      return {
        isValid: false,
        errorMessage: `Validation Error: ES phase ${es} is not between two ED phases.`,
        typeOfError: 'validation_error',
      };
    }
  }

  // Handle edge case where breath-hold crop causes cycle to start or end with an ES
  const firstED = edIdx[0];
  const secondED = edIdx[1];
  const firstES = esIdx[0];
  const lastED = edIdx[edIdx.length - 1];
  const lastES = esIdx[esIdx.length - 1];
  const beforeLastED = edIdx[edIdx.length - 2];
  let startCrop = firstED;
  let endCrop = lastED;
  let earlyReturnForCrop = false;
  let errorMsg = '';

  if (edIdx.length > 2 && firstES > secondED && firstES > firstED) {
    earlyReturnForCrop = true;
    errorMsg = 'Validation Error: The first ES phase is after the second ED phase, which indicates that the first cycle starts with an ES.';
    startCrop = secondED;
  }

  if (edIdx.length > 2 && lastES < beforeLastED && lastES < lastED) {
    earlyReturnForCrop = true;
    const endMsg = 'Validation Error: The last ES phase is before the second-to-last ED phase, which indicates that the last cycle ends with an ES.';
    errorMsg = errorMsg ? `${errorMsg} ${endMsg}` : endMsg;
    endCrop = beforeLastED;
  }

  if (earlyReturnForCrop) {
    return {
      isValid: false,
      errorMessage: errorMsg,
      typeOfError: 'suggested_action',
      suggestedAction: {
        action: 'crop',
        crop: {
          start: startCrop,
          end: endCrop,
        },
      },
    };
  }

  // Check that indices are in ascending order and alternate
  interface MarkedIndex { idx: number; type: 'ES' | 'ED' };
  const allIndices: MarkedIndex[] = [
    ...esIdx.map(idx => ({ idx, type: 'ES' as const })),
    ...edIdx.map(idx => ({ idx, type: 'ED' as const })),
  ].sort((a, b) => a.idx - b.idx);

  for (let i = 1; i < allIndices.length; i++) {
    if (allIndices[i].idx <= allIndices[i - 1].idx) {
      return {
        isValid: false,
        errorMessage: 'Validation Error: ES-ED phases are not in ascending order.',
        typeOfError: 'validation_error',
      };
    }
    if (allIndices[i].type === 'ES' && allIndices[i - 1].type === 'ES') {
      return {
        isValid: false,
        errorMessage: 'Validation Error: Two ES phases are adjacent without an ED phase in between.',
        typeOfError: 'validation_error',
      };
    }
    if (allIndices[i].type === 'ED' && allIndices[i - 1].type === 'ED') {
      return {
        isValid: false,
        errorMessage: 'Validation Error: Two ED phases are adjacent without an ES phase in between.',
        typeOfError: 'validation_error',
      };
    }
  }

  return { isValid: true, errorMessage: '' };
}

/**
 * Extracts LV blood pool area for an image using endocardial polygon or mask.
 */
export function extractLvBpAreaForImage(
  imageUID: string,
  orientation: Orientation,
  viewerID: string,
): number {
  const containerStore = useContourContainerStore();
  const container = containerStore.contourContainers[imageUID];
  if (!container) return 0;

  const { endoContourId } = useMaskExtractor().getContourIdsForOrientation(orientation, viewerID);

  // 1. Try polygon area (instant Shoelace calculation on contour vertices)
  try {
    const poly = container.getContourPointArray(endoContourId);
    if (poly && poly.length >= 3) {
      const area = getPolygonArea(poly);
      if (area > 0) return area;
    }
  }
  catch {
    // ignore
  }

  // 2. Try mask pixel count
  try {
    const mask = container.getContourMaskCompleteIn(endoContourId);
    if (mask && mask.length > 0) {
      return mask.length;
    }
  }
  catch {
    // ignore
  }

  return 0;
}

/**
 * Performs full ES/ED detection and validation pre-check for a single slice before analysis.
 */
export function checkSliceEsEd(params: SlicePrecheckParams): SliceEsEdPrecheckResult {
  const currentStudyStore = useCurrentStudyStore();
  const ocgCalculatorStore = useOCGCalculatorStore();
  const viewportActions = useViewportActions();

  const studyObj = viewportActions.getStudyObjectByViewerId(params.viewerID);
  if (!studyObj) {
    return {
      isValid: false,
      hasCropSuggestion: false,
      errorMessage: `Could not retrieve study object for viewer ${params.viewerID}`,
      esIndices: [],
      edIndices: [],
      nRR: 0,
      lvBpAreaRaw: [],
    };
  }

  const breathholdState = studyObj.getBreathhold();
  const excludedImages = studyObj.getExcludedImages();
  const imageUIDs = currentStudyStore.getSliceImageUIDsbyView(params.viewerID) ?? [];

  const start = breathholdState.getStart();
  const end = breathholdState.getEnd();

  if (start < 0 || end < start || end >= imageUIDs.length) {
    return {
      isValid: false,
      hasCropSuggestion: false,
      errorMessage: `Invalid breathhold range (${start + 1} - ${end + 1}) for slice.`,
      esIndices: [],
      edIndices: [],
      nRR: 0,
      lvBpAreaRaw: [],
    };
  }

  // Check if OCGCalculatorStore already has computed curves for this slice
  const cachedCurves = ocgCalculatorStore.getCurves(params.seriesIndex, params.sliceIndex);
  let lvBpAreaRaw: number[] = [];
  const excludedLocalIndices: number[] = [];

  if (cachedCurves && cachedCurves.lvBpArea && cachedCurves.lvBpArea.length === (end - start + 1)) {
    lvBpAreaRaw = [...cachedCurves.lvBpArea];
    for (let p = 0; p < lvBpAreaRaw.length; p++) {
      const globalIdx = start + p;
      const uid = imageUIDs[globalIdx];
      if (excludedImages.has(uid)) {
        excludedLocalIndices.push(p);
      }
    }
  }
  else {
    for (let p = 0; p <= (end - start); p++) {
      const globalIdx = start + p;
      const uid = imageUIDs[globalIdx];

      if (excludedImages.has(uid)) {
        excludedLocalIndices.push(p);
        lvBpAreaRaw.push(Number.NaN);
      }
      else {
        const area = extractLvBpAreaForImage(uid, params.orientation, params.viewerID);
        lvBpAreaRaw.push(area > 0 ? area : Number.NaN);
      }
    }
  }

  const validAreaCount = lvBpAreaRaw.filter(v => Number.isFinite(v) && v > 0).length;
  if (validAreaCount < 3) {
    return {
      isValid: false,
      hasCropSuggestion: false,
      errorMessage: 'Insufficient endocardial segmentations to detect cardiac cycles.',
      esIndices: [],
      edIndices: [],
      nRR: 0,
      lvBpAreaRaw,
    };
  }

  // Detect cardiac phases using store algorithm
  const { esIndices, edIndices, nRR, lvBpAreaSmoothed } = detectCardiacEsEdFromArea(
    lvBpAreaRaw,
    excludedLocalIndices,
  );

  // Validate the detected ES/ED indices
  const validation = validateEsEdIndices(esIndices, edIndices, nRR);

  let suggestedCrop: SuggestedCropInfo | undefined;
  if (validation.suggestedAction?.crop) {
    const startPhase = validation.suggestedAction.crop.start;
    const endPhase = validation.suggestedAction.crop.end;
    const startImageIndex = start + startPhase;
    const endImageIndex = start + endPhase;

    suggestedCrop = {
      startPhase,
      endPhase,
      startImageIndex,
      endImageIndex,
      startUID: imageUIDs[startImageIndex] ?? '',
      endUID: imageUIDs[endImageIndex] ?? '',
    };
  }

  return {
    isValid: validation.isValid,
    hasCropSuggestion: Boolean(suggestedCrop),
    errorMessage: validation.errorMessage,
    esIndices,
    edIndices,
    nRR,
    lvBpAreaRaw,
    lvBpAreaSmoothed,
    excludedPhaseIndices: excludedLocalIndices,
    suggestedCrop,
  };
}
